// ─── How it works — Sơ đồ kiến trúc trực quan ───────────────
// Diagram dạng lớp (layered) dựng bằng div + CSS, không dùng SVG.
// Thể hiện luồng dữ liệu cTrader → gateway → frontend → backend/telegram.

export interface LayerNodeProps {
  icon: string;
  tone: 'primary' | 'accent' | 'secondary' | 'emerald';
  title: string;
  subtitle: string;
  chips?: string[];
}

const TONES: Record<LayerNodeProps['tone'], { bar: string; iconBg: string; icon: string; border: string }> = {
  primary: { bar: 'bg-primary-500', iconBg: 'bg-primary-500/10', icon: 'text-primary-400', border: 'border-primary-500/25' },
  accent: { bar: 'bg-accent-500', iconBg: 'bg-accent-500/10', icon: 'text-accent-400', border: 'border-accent-500/25' },
  secondary: { bar: 'bg-secondary-500', iconBg: 'bg-secondary-500/10', icon: 'text-secondary-400', border: 'border-secondary-500/25' },
  emerald: { bar: 'bg-emerald-500', iconBg: 'bg-emerald-500/10', icon: 'text-emerald-400', border: 'border-emerald-500/25' },
};

export function LayerNode({ icon, tone, title, subtitle, chips }: LayerNodeProps) {
  const t = TONES[tone];
  return (
    <div className={`relative flex items-stretch gap-3 bg-background-50 rounded-xl border ${t.border} overflow-hidden`}>
      <span className={`w-1 shrink-0 ${t.bar}`}></span>
      <div className="flex items-center gap-3 py-3.5 pr-4 flex-1 min-w-0">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${t.iconBg}`}>
          <i className={`${icon} ${t.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
        </div>
        <div className="min-w-0">
          <p className="text-foreground-950 text-sm font-semibold">{title}</p>
          <p className="text-foreground-600 text-xs leading-relaxed mt-0.5">{subtitle}</p>
          {chips && chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {chips.map((c) => (
                <span key={c} className="inline-flex items-center px-2 py-0.5 rounded-md bg-background-200/70 text-foreground-600 text-[10px] font-mono">
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Connector({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center py-1.5">
      <span className="w-px h-3 bg-background-300/70"></span>
      <div className="w-6 h-6 rounded-full bg-background-100 border border-background-300/70 flex items-center justify-center my-0.5">
        <i className="ri-arrow-down-line text-foreground-400 text-xs w-4 h-4 flex items-center justify-center"></i>
      </div>
      <span className="w-px h-3 bg-background-300/70"></span>
      {label && (
        <span className="mt-1 px-2 py-0.5 rounded bg-background-200/70 text-foreground-500 text-[10px] font-mono">
          {label}
        </span>
      )}
    </div>
  );
}

export default function ArchitectureDiagram() {
  return (
    <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
      <div className="flex items-center gap-2 mb-5">
        <i className="ri-flow-chart text-primary-400 text-base w-5 h-5 flex items-center justify-center"></i>
        <p className="text-sm font-heading font-semibold text-foreground-900">Sơ đồ kiến trúc &amp; luồng dữ liệu</p>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Layer 1: nguồn dữ liệu */}
        <LayerNode
          icon="ri-bank-line"
          tone="secondary"
          title="cTrader (Spotware) — Open API"
          subtitle="Nguồn giá bid/ask theo từng tick và nến (trendbar). Nhận lệnh đặt/đóng/sửa SLTP từ hệ thống."
          chips={['tick giá', 'trendbar', 'place order']}
        />

        <Connector label="WebSocket (OAuth 2.0)" />

        {/* Layer 2: gateway */}
        <LayerNode
          icon="ri-plug-line"
          tone="accent"
          title="ctrader-gateway — Edge Function"
          subtitle="Cầu nối duy nhất tới Open API. Mở WebSocket, xác thực token, tự refresh khi hết hạn, rồi trả về SSE stream."
          chips={['auth', 'refresh token', 'SSE']}
        />

        <Connector label="SSE stream (server-sent events)" />

        {/* Layer 3: frontend engines */}
        <div className="relative bg-background-50 rounded-xl border border-primary-500/25 overflow-hidden">
          <span className="absolute inset-y-0 left-0 w-1 bg-primary-500"></span>
          <div className="pl-4 pr-4 py-3.5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0">
                <i className="ri-window-2-line text-primary-400 text-lg w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div>
                <p className="text-foreground-950 text-sm font-semibold">Frontend — React SPA (4 engine)</p>
                <p className="text-foreground-600 text-xs leading-relaxed mt-0.5">
                  Toàn bộ logic xử lý ngay trong trình duyệt theo từng tick. Mỗi engine chạy độc lập theo tài khoản.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
              {[
                { icon: 'ri-radar-line', label: 'Chin Bot' },
                { icon: 'ri-shield-check-line', label: 'Trailing Stop' },
                { icon: 'ri-swap-line', label: 'Copy Trade' },
                { icon: 'ri-pulse-line', label: 'Signal' },
              ].map((e) => (
                <div key={e.label} className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-primary-500/5 border border-primary-500/15">
                  <i className={`${e.icon} text-primary-400 text-xs w-4 h-4 flex items-center justify-center`}></i>
                  <span className="text-foreground-700 text-[10px] font-semibold whitespace-nowrap">{e.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Split into two outputs */}
        <div className="flex items-stretch gap-4 mt-1.5">
          <div className="flex flex-col items-center flex-1">
            <span className="w-px h-3 bg-background-300/70"></span>
            <div className="w-6 h-6 rounded-full bg-background-100 border border-background-300/70 flex items-center justify-center my-0.5">
              <i className="ri-arrow-down-line text-foreground-400 text-xs w-4 h-4 flex items-center justify-center"></i>
            </div>
          </div>
          <div className="flex flex-col items-center flex-1">
            <span className="w-px h-3 bg-background-300/70"></span>
            <div className="w-6 h-6 rounded-full bg-background-100 border border-background-300/70 flex items-center justify-center my-0.5">
              <i className="ri-arrow-down-line text-foreground-400 text-xs w-4 h-4 flex items-center justify-center"></i>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LayerNode
            icon="ri-database-2-line"
            tone="primary"
            title="Supabase"
            subtitle="Đăng nhập (Auth), lưu cấu hình/workspace/trạng thái bot (Database), chạy edge functions."
            chips={['Auth', 'Database', 'Edge Functions']}
          />
          <LayerNode
            icon="ri-telegram-line"
            tone="emerald"
            title="Telegram Alert"
            subtitle="Nhận nội dung từ engine, đọc bot token/chat id, gửi qua Telegram API (có chống trùng lặp)."
            chips={['Status + Note', 'dedup']}
          />
        </div>

        {/* Loop-back note */}
        <div className="mt-4 flex items-center justify-center gap-2 text-foreground-500 text-[11px]">
          <i className="ri-loop-left-line text-secondary-400 w-4 h-4 flex items-center justify-center"></i>
          <span>
            Lệnh đặt/đóng/sửa SLTP đi <strong className="text-foreground-700">ngược lại</strong> qua gateway về cTrader (chiều mũi tên ngược).
          </span>
        </div>
      </div>
    </div>
  );
}